import { Link } from "react-router-dom";
import { ShieldCheck, Clock, BadgeDollarSign, MousePointerClick, Receipt, MapPin, Home as HomeIcon, Building2, Sparkles, Brush, Wand2, ArrowRight } from "lucide-react";
import { useSeo, useJsonLd } from "@/hooks/useSeo";
import { TrustCounter, CallOnlyCard, useServiceGroups } from "@/components/marketing/ServiceCards";
import { FestivalBanner } from "@/components/marketing/FestivalBanner";
import { money } from "@/lib/api";
import { SectionHeader, PageHero, BookNowButton, CtaBand, FallBanner, ServiceAreasGrid, FaqList, Testimonials, PriceTable, IncludesList, useCatalogParts } from "@/components/marketing/Sections";

const WHY = [
  [Sparkles, "Professional Cleaning", "Trained, background-checked cleaners who treat your home like their own."],
  [ShieldCheck, "Reliable Service", "On-time arrivals, live booking status and a team you can count on."],
  [BadgeDollarSign, "Affordable Prices", "Fair, upfront rates with recurring and supplies discounts."],
  [MousePointerClick, "Easy Online Booking", "Book in minutes from your phone — any time, 24/7."],
  [Receipt, "Transparent Pricing", "See your itemized price before you confirm. No surprises."],
  [MapPin, "Local GTA Service", "Proudly serving Brampton, Mississauga and communities across the GTA."],
];
const STEPS = ["Tell us about your home", "Get your price", "Pick a date and time", "We clean your home", "View your invoice and rate your service"];
const POPULAR = [
  ["Standard Cleaning", "Routine upkeep for a fresh, tidy home.", Sparkles, "standard"],
  ["Deep Cleaning", "A detailed, top-to-bottom reset.", Brush, "deep"],
  ["Apartment Cleaning", "Right-sized cleaning for condos and apartments.", Building2, "standard"],
  ["House Cleaning", "Whole-home cleaning for townhouses and houses.", HomeIcon, "standard"],
  ["Custom Cleaning", "Tell us what matters most — we'll tailor the visit.", Wand2, "standard"],
];

export default function Home() {
  const { settings, fallCampaign, standardGeneral, deepGeneral, areas, faqs, testimonials, services } = useCatalogParts();
  useSeo("Professional Home Cleaning in Brampton, Mississauga & the GTA", "Book professional residential cleaning across the GTA online in minutes. Transparent pricing, reliable cleaners, standard and deep cleaning. Kaur Cleaning Services.");
  useJsonLd("ld-business", settings && { "@context": "https://schema.org", "@type": "HomeAndConstructionBusiness", name: settings.business_name, description: settings.tagline, telephone: settings.phone || undefined, email: settings.email || undefined, url: window.location.origin, priceRange: "$$", currenciesAccepted: "CAD", areaServed: areas.map((a) => ({ "@type": "City", name: a.city })), makesOffer: services.map((s) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: s.name, description: s.short_description } })) });
  const homeFaqs = faqs.filter((f) => f.show_on_home).slice(0, 4);
  const activeServiceNames = services.map((s) => s.name);
  const { online, callOnly } = useServiceGroups();

  return (
    <div>
      {fallCampaign?.theme === "festival" && <FestivalBanner campaign={fallCampaign} />}
      <section className="relative overflow-hidden grain">
        <div className="kc-container grid lg:grid-cols-12 gap-10 items-center py-16 sm:py-24">
          <div className="lg:col-span-7">
            <p className="kc-eyebrow rise">{settings?.service_region_label || "Serving the GTA & Surrounding Areas"}</p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold text-[var(--kc-text)] leading-[1.05] rise rise-1" data-testid="hero-headline">
              A Cleaner Home.<br /><span className="text-[var(--kc-blue)]">A Happier You.</span>
            </h1>
            <p className="mt-6 text-lg text-[#5A606A] max-w-xl leading-relaxed rise rise-2">Professional residential cleaning throughout the GTA and surrounding areas. Transparent pricing, reliable cleaners, and booking that takes minutes.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 rise rise-3">
              <BookNowButton source="hero" label="BOOK A CLEANING" testId="hero-book-now-button" />
              <Link to="/pricing" data-testid="hero-view-pricing-button" className="btn-secondary">VIEW PRICING</Link>
            </div>
            <div className="mt-8 rise rise-3"><TrustCounter /></div>
            <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm font-medium">
              <li className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-[var(--kc-gold)]" />24/7 Online Booking</li>
              <li className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[var(--kc-gold)]" />Transparent Pricing</li>
              <li className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-[var(--kc-gold)]" />Local GTA Team</li>
            </ul>
          </div>
          <div className="lg:col-span-5 relative">
            <img src="https://images.unsplash.com/photo-1628744876497-eb30460be9f6?crop=entropy&cs=srgb&fm=jpg&q=80&w=900" alt="Bright, freshly cleaned living room" className="rounded-3xl object-cover w-full aspect-[4/5] shadow-2xl" loading="eager" />
            <div className="absolute -bottom-6 left-4 sm:-left-8 kc-card p-5 max-w-[240px]">
              <p className="text-xs uppercase tracking-wider text-[#5A606A]">Standard cleaning from</p>
              <p className="text-3xl font-display font-semibold text-[var(--kc-blue)]" data-testid="hero-starting-price">{standardGeneral[0] ? money(standardGeneral[0].starting_price) : "—"}</p>
              <p className="text-xs text-[#5A606A] mt-1">All prices in {settings?.currency || "CAD"}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="kc-section pt-20" id="why">
        <div className="kc-container">
          <SectionHeader eyebrow="Why Kaur" title="Why Choose Kaur Cleaning Services" intro="Clean spaces. Brighter days. Happier homes — that's the promise behind every visit." />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="why-choose-grid">
            {WHY.map(([Icon, title, text], i) => (
              <div key={title} className={`kc-card p-7 ${i % 3 === 1 ? "lg:translate-y-6" : ""}`}>
                <span className="h-11 w-11 rounded-full bg-[#F3F1EC] grid place-items-center"><Icon className="w-5 h-5 text-[var(--kc-blue)]" /></span>
                <h3 className="mt-5 text-lg font-semibold">{title}</h3><p className="mt-2 text-sm text-[#5A606A] leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="kc-section bg-white border-y border-[#E5E2DA]">
        <div className="kc-container">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeader eyebrow="Services" title="Popular Cleaning Services" />
            <Link to="/services" className="btn-ghost" data-testid="home-all-services-link">All services <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-5" data-testid="popular-services-grid">
            {POPULAR.filter(([, , , code]) => !services.length || online.some((s) => s.code === code)).map(([title, text, Icon]) => (
              <Link to="/services" key={title} className="group rounded-2xl border border-[#E5E2DA] bg-[var(--kc-bg)] p-6 hover:bg-[var(--kc-blue)] hover:border-[var(--kc-blue)] transition-colors duration-200" data-testid={`popular-service-${title.toLowerCase().replace(/\s/g, "-")}`}>
                <Icon className="w-6 h-6 text-[var(--kc-terracotta)] group-hover:text-[#FBBF24] transition-colors" />
                <h3 className="mt-4 font-semibold group-hover:text-white transition-colors">{title}</h3>
                <p className="mt-1 text-sm text-[#5A606A] group-hover:text-white/75 transition-colors">{text}</p>
              </Link>
            ))}
          </div>
          {activeServiceNames.length > 0 && <p className="mt-6 text-xs text-[#5A606A]">Book online: {online.map((s) => s.name).join(" · ")}</p>}
          {callOnly.length > 0 && (
            <div className="mt-12" data-testid="home-call-only-section">
              <SectionHeader eyebrow="Custom quote · Call to book" title="Specialty cleaning by quote" intro="These services are priced after a quick call — no online pricing." />
              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{callOnly.map((s) => <CallOnlyCard key={s.code} s={s} />)}</div>
            </div>
          )}
        </div>
      </section>

      <section className="kc-section">
        <div className="kc-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><SectionHeader eyebrow="How it works" title="Five simple steps" intro="From first tap to sparkling home, everything happens in one place — including your invoice and rating." /><BookNowButton source="how-it-works" className="btn-primary mt-8" /></div>
          <ol className="lg:col-span-8 grid sm:grid-cols-2 gap-4" data-testid="how-it-works-list">
            {STEPS.map((s, i) => (
              <li key={s} className="kc-card p-6 flex gap-4 items-start"><span className="font-display text-3xl font-semibold text-[var(--kc-terracotta)] leading-none">{i + 1}</span><p className="font-medium pt-1">{s}</p></li>
            ))}
          </ol>
        </div>
      </section>

      <section className="kc-section bg-white border-y border-[#E5E2DA]">
        <div className="kc-container">
          <SectionHeader eyebrow="Pricing preview" title="Transparent pricing, loaded live from our rate card" intro='Final price depends on bathrooms, condition, supplies, distance and add-ons — that’s why we say "starting from".' />
          <div className="mt-10 grid md:grid-cols-2 gap-8">
            <PriceTable title="Standard Cleaning" packages={standardGeneral} testId="pricing-preview-standard" />
            <PriceTable title="Deep Cleaning" packages={deepGeneral} testId="pricing-preview-deep" />
          </div>
          <div className="mt-10"><Link to="/pricing" className="btn-secondary" data-testid="pricing-preview-link">See full pricing</Link></div>
        </div>
      </section>

      {fallCampaign && fallCampaign.theme !== "festival" && <section className="kc-container pt-16 sm:pt-24"><FallBanner campaign={fallCampaign} /></section>}

      <section className="kc-section">
        <div className="kc-container">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeader eyebrow="Service areas" title={settings?.service_region_label || "Serving the GTA & Surrounding Areas"} />
            <Link to="/service-areas" className="btn-ghost" data-testid="home-all-areas-link">All areas <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="mt-10" data-testid="home-service-areas"><ServiceAreasGrid areas={areas} compact /></div>
        </div>
      </section>

      {testimonials.length > 0 && (
        <section className="kc-section bg-[#F3F1EC]">
          <div className="kc-container"><SectionHeader eyebrow="Reviews" title="What GTA families say" /><div className="mt-12" data-testid="home-testimonials"><Testimonials items={testimonials} /></div></div>
        </section>
      )}

      {homeFaqs.length > 0 && (
        <section className="kc-section">
          <div className="kc-container grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4"><SectionHeader eyebrow="FAQ" title="Good to know" /><Link to="/faq" className="btn-ghost mt-6" data-testid="home-all-faq-link">All questions <ArrowRight className="w-4 h-4" /></Link></div>
            <div className="lg:col-span-8" data-testid="home-faq"><FaqList faqs={homeFaqs} testPrefix="home-faq" /></div>
          </div>
        </section>
      )}

      <CtaBand source="home-final" />
    </div>
  );
}
